import { useMemo } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { AriaLabelingContext } from '../core/context/AriaLabelingContext.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_TRUNCATION_MODE,
  DISPLAY_NAME,
} from './constants.js';
import { SankeyCustomTooltipContext } from './context/SankeyCustomTooltip.context.js';
import { SankeySeriesActionsContext } from './context/SankeySeriesActions.context.js';
import { SankeyConfigProvider } from './providers/SankeyConfigProvider.js';
import { SankeyDataProvider } from './providers/SankeyDataProvider.js';
import { SankeyFormatterProvider } from './providers/SankeyFormatterProvider.js';
import { SankeyChartLayout } from './SankeyChart.js';
import { Nodes } from './slots/Nodes.js';
import { Tooltip } from './slots/Tooltip.js';
import type { SankeyChartProps } from './types/sankey-props.js';
import { iterateSlots } from './utils/iterate-slots.js';

/**
 * @internal
 */
export const SankeyChartRoot = (props: SankeyChartProps) => {
  const {
    data,
    sourceAccessor,
    targetAccessor,
    valueAccessor,
    nodes,
    nodeIdAccessor,
    nodeLabelAccessor,
    columnAccessor,
    width,
    height,
    loading = false,
    truncationMode = DEFAULT_TRUNCATION_MODE,
    colorPalette = 'categorical-vivid',
    formatter,
    unit,
    columnLabels,
    seriesActions,
    children,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);
  const { nodes: nodesConfig, tooltip: tooltipConfig } = iterateSlots(children);
  const chartHeight = coerceSizeValue(height ?? DEFAULT_CHART_HEIGHT);
  const ariaLabelingValue = useMemo(
    () => ({
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
    }),
    [ariaLabel, ariaLabelledBy, ariaDescribedBy, ariaDetails],
  );

  return (
    <AriaLabelingContext.Provider value={ariaLabelingValue}>
      <SankeyFormatterProvider formatter={formatter} unit={unit}>
        <SankeyCustomTooltipContext.Provider value={tooltipConfig}>
          <SankeyConfigProvider
            nodesConfig={nodesConfig}
            truncationMode={truncationMode}
            colorPalette={colorPalette}
            columnLabels={columnLabels}
          >
            <SankeyDataProvider
              data={data}
              sourceAccessor={sourceAccessor}
              targetAccessor={targetAccessor}
              valueAccessor={valueAccessor}
              nodes={nodes}
              nodeIdAccessor={nodeIdAccessor}
              nodeLabelAccessor={nodeLabelAccessor}
              columnAccessor={columnAccessor}
            >
            <SankeySeriesActionsContext.Provider value={seriesActions}>
                <SankeyChartLayout
                  {...behavioralTrackingProps}
                  chartWidth={width}
                  chartHeight={chartHeight}
                  showLoader={loading}
                >
                  {children}
                </SankeyChartLayout>
            </SankeySeriesActionsContext.Provider>
            </SankeyDataProvider>
          </SankeyConfigProvider>
        </SankeyCustomTooltipContext.Provider>
      </SankeyFormatterProvider>
    </AriaLabelingContext.Provider>
  );
};

(
  SankeyChartRoot as typeof SankeyChartRoot & { displayName: string }
).displayName = DISPLAY_NAME;

/**
 * The `SankeyChart` visualizes directed flows between nodes across stages.
 * @internal
 */
export const SankeyChart = Object.assign(SankeyChartRoot, {
  Nodes,
  Tooltip,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});

(SankeyChart as typeof SankeyChart & { displayName: string }).displayName =
  'SankeyChart';
